import React, { useState, useEffect } from 'react';
import { StopSelector } from '../components/StopSelector';
import { RouteResults, RouteResult } from '../components/RouteResults';
import { ItineraryPanel } from '../components/ItineraryPanel';
import { MapPlaceholder } from '../components/MapPlaceholder';
import { DraggablePanel } from '../components/DraggablePanel';
import { DepartureBoardPanel } from '../components/DepartureBoardPanel';
import { RouteSearchService } from '../services/routeSearchService';
import { useBookmark } from '../contexts/BookmarkContext';
import routesData from '../data/routes.json';
import stopsData from '../data/stops.json';
import { RoutesData, StopsData } from '../types/ferry';
import { useLanguage } from '../contexts/LanguageContext';
import { logger } from '../utils/logger';
import { analytics } from '../utils/analytics';

export const HomePage: React.FC = () => {
  const { language, t } = useLanguage();
  useBookmark(); // Initialize bookmark context
  const [fromStop, setFromStop] = useState('takamatsu'); // Default: Takamatsu (mainland hub)
  const [toStop, setToStop] = useState('naoshima_miyanoura'); // Default: Naoshima (popular destination)
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [results, setResults] = useState<RouteResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [seasonalWarnings, setSeasonalWarnings] = useState<string[]>([]);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<'search' | 'trips' | 'map'>('search');
  const [planningTab, setPlanningTab] = useState<'route-search' | 'departure-board'>('route-search');
  const [selectedDeparturePort, setSelectedDeparturePort] = useState<string | null>(null);
  const [hoveredRouteId, setHoveredRouteId] = useState<string | null>(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [searchService] = useState(() => new RouteSearchService(routesData as RoutesData, stopsData as StopsData));
  const [autoSearchTriggered, setAutoSearchTriggered] = useState(false);

  // Initialize with current date/time and trigger auto-search
  useEffect(() => {
    const now = new Date();
    // Round time to next 30 minutes for better UX
    const minutes = now.getMinutes();
    const roundedMinutes = minutes < 30 ? 30 : 0;
    const roundedHours = minutes < 30 ? now.getHours() : now.getHours() + 1;

    setSelectedDate(now.toISOString().split('T')[0]);
    setSelectedTime(`${roundedHours.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`);

    // Trigger automatic search after initial setup
    setAutoSearchTriggered(true);
  }, []);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-trigger search when all default values are set
  useEffect(() => {
    if (autoSearchTriggered && fromStop && toStop && selectedDate && selectedTime) {
      // Small delay to ensure UI is ready
      setTimeout(() => {
        searchRoutes();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearchTriggered, selectedDate, selectedTime]);

  const searchRoutes = () => {
    if (!fromStop || !toStop || !selectedDate || !selectedTime) {
      setError(t.selectAll);
      return;
    }

    if (fromStop === toStop) {
      setError(t.selectDifferent);
      setResults([]);
      return;
    }

    setIsLoading(true);
    setError('');
    setHasSearched(true);

    try {
      const searchDate = new Date(selectedDate);
      logger.log('Searching routes:', { fromStop, toStop, selectedDate, selectedTime });

      const routeResults = searchService.searchDirectRoutes(fromStop, toStop, searchDate, selectedTime);
      logger.log('Route results:', routeResults);

      // Get seasonal warnings
      const warnings = searchService.getSeasonalWarnings(fromStop, toStop, searchDate);
      setSeasonalWarnings(warnings);

      setResults(routeResults);

      // Track search analytics
      analytics.searchRoute({
        fromStop,
        toStop,
        date: selectedDate,
        resultsCount: routeResults.length,
      });

      if (routeResults.length === 0) {
        if (searchService.areStopsDirectlyConnected(fromStop, toStop)) {
          if (warnings.length > 0) {
            setError(`No services available. ${warnings.join(', ')}`);
          } else {
            setError(t.noFerriesFound);
          }
        } else {
          setError(t.noDirectRoutes);
        }
      }
    } catch (err) {
      setError(t.errorSearching);
      logger.error('Route search error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const canSearch = fromStop && toStop && selectedDate && selectedTime;

  const swapStops = () => {
    const temp = fromStop;
    setFromStop(toStop);
    setToStop(temp);
  };

  const handleMapPortClick = (portId: string) => {
    // If portId is empty, clear the selection
    if (!portId) {
      setSelectedDeparturePort(null);
      return;
    }

    // Expand panel if collapsed
    if (leftPanelCollapsed) {
      setLeftPanelCollapsed(false);
    }
    // Switch to search tab on mobile
    setActiveTab('search');
    // Switch to departure board tab
    setPlanningTab('departure-board');
    // Set the selected port
    setSelectedDeparturePort(portId);
  };

  const handleDepartureBoardPlanRoute = (fromId: string, toId: string) => {
    // Switch back to route search tab
    setPlanningTab('route-search');
    // Set the from/to stops
    setFromStop(fromId);
    setToStop(toId);
    // Clear the selected departure port to remove highlighting
    setSelectedDeparturePort(null);
  };

  return (
    <>
      {/* Mobile Tab Navigation */}
      <div className="lg:hidden max-w-lg md:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto px-4 mb-6">
        <div className="flex bg-white rounded-lg shadow-sm">
          <button
            onClick={() => setActiveTab('search')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors rounded-l-lg ${
              activeTab === 'search'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:text-blue-600'
            }`}>
              <div className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </div>
            </button>
          <button
            onClick={() => setActiveTab('trips')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'trips'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:text-blue-600'
            }`}>
              <div className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                My Trips
              </div>
            </button>
          <button
            onClick={() => setActiveTab('map')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors rounded-r-lg ${
              activeTab === 'map'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:text-blue-600'
            }`}>
              <div className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Map
              </div>
            </button>
        </div>
      </div>

      {/* Full-Width Map Background Layer (Layer 1) */}
      <main className="flex-1 flex overflow-hidden">
        <div className="relative flex-1">
          <div className={`absolute inset-0 z-0 ${activeTab === 'map' ? 'block' : 'hidden lg:block'}`}>
            <MapPlaceholder
              className="h-full"
              setFromStop={setFromStop}
              setToStop={setToStop}
              onDeparturePortClick={handleMapPortClick}
              externalSelectedPort={selectedDeparturePort}
              hoveredRouteId={hoveredRouteId}
            />
          </div>

          {/* Search Panel Overlay (Layer 2) */}
          <div
            className={`absolute top-0 left-0 bottom-0 z-20 transition-all duration-300 ${leftPanelCollapsed ? 'w-12' : 'w-full max-w-lg md:max-w-2xl lg:w-96'} ${activeTab === 'search' ? 'block' : 'hidden lg:block'} overflow-y-auto lg:mx-0 border border-gray-200`}
            style={{backgroundColor: '#F4F5F7'}}
          >
            {/* Collapse button on right edge, vertically centered */}
            <button
              onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
              className="hidden lg:flex sticky top-1/2 -translate-y-1/2 float-right -mr-px z-30 px-0 py-6 bg-white border border-gray-300 rounded-l-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors items-center justify-center"
              aria-label={leftPanelCollapsed ? "Expand" : "Collapse"}
            >
              <svg
                className={`w-4 h-4 transition-transform ${leftPanelCollapsed ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between min-w-0">
                {!leftPanelCollapsed && (
                  <h1 className="text-lg font-semibold text-gray-900">{t.planYourJourney}</h1>
                )}
              </div>

              {/* Planning Mode Tabs */}
              {!leftPanelCollapsed && (
                <div className="flex bg-white rounded-lg shadow-sm border border-gray-200">
                  <button
                    onClick={() => {
                      setPlanningTab('route-search');
                      setSelectedDeparturePort(null); // Clear port highlighting when switching to route search
                    }}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors rounded-l-lg ${
                      planningTab === 'route-search'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:text-blue-600'
                    } ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}
                  >
                    {language === 'zh' ? '路线搜索' : language === 'ja' ? 'ルート検索' : 'Route Search'}
                  </button>
                  <button
                    onClick={() => setPlanningTab('departure-board')}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors rounded-r-lg ${
                      planningTab === 'departure-board'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:text-blue-600'
                    } ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}
                  >
                    {language === 'zh' ? '出发时刻表' : language === 'ja' ? '出発時刻表' : 'Departure Board'}
                  </button>
                </div>
              )}

              {/* Route Search Tab Content */}
              {!leftPanelCollapsed && planningTab === 'route-search' && (
                <div className="space-y-4">
                  {/* From/To selectors with compact spacing */}
                  <div>
                    {/* From selector */}
                    <StopSelector
                      stops={stopsData as StopsData}
                      selectedStop={fromStop}
                      onStopSelect={setFromStop}
                      placeholder={t.wherePlaceholder}
                      label={t.from}
                    />

                    {/* Swap button */}
                    <div className="flex justify-center pt-1">
                      <button
                        onClick={swapStops}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={!fromStop && !toStop}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                        </svg>
                      </button>
                    </div>

                    {/* To selector */}
                    <StopSelector
                      stops={stopsData as StopsData}
                      selectedStop={toStop}
                      onStopSelect={setToStop}
                      placeholder={t.wherePlaceholder}
                      label={t.to}
                    />
                  </div>

                  {/* Date/Time row */}
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <label className={`block text-sm font-medium text-gray-700 mb-2 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                        {t.date}
                      </label>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <label className={`block text-sm font-medium text-gray-700 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                          {t.time}
                        </label>
                        <button
                          onClick={() => {
                            // Use Japan Standard Time (JST = UTC+9)
                            const now = new Date();
                            const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
                            const jstTime = new Date(utc + (3600000 * 9)); // Add 9 hours for JST

                            setSelectedDate(jstTime.toISOString().split('T')[0]);
                            const hours = jstTime.getHours().toString().padStart(2, '0');
                            const minutes = jstTime.getMinutes().toString().padStart(2, '0');
                            setSelectedTime(`${hours}:${minutes}`);
                          }}
                          className={`text-sm text-blue-600 hover:text-blue-800 font-medium ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}
                        >
                          Now
                        </button>
                      </div>
                      <input
                        type="time"
                        value={selectedTime}
                        onChange={(e) => setSelectedTime(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Search Button */}
                  <div className="border-t border-gray-200 pt-3">
                    <button
                      onClick={searchRoutes}
                      disabled={!canSearch || isLoading}
                      className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-colors ${
                        canSearch && !isLoading
                          ? 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                          : 'bg-gray-300 cursor-not-allowed'
                      }`}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          {t.searchFerries}...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          {t.searchFerries}
                        </div>
                      )}
                    </button>

                    {!canSearch && (
                      <p className="text-sm text-gray-500 text-center mt-2">
                        {t.selectAll}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Route Results Section */}
              {hasSearched && !leftPanelCollapsed && planningTab === 'route-search' && (
                <div className="border-t border-gray-200 pt-6">
                  <RouteResults
                    results={results}
                    isLoading={isLoading}
                    error={error}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    seasonalWarnings={seasonalWarnings}
                    stops={stopsData as StopsData}
                    fromStopId={fromStop}
                    toStopId={toStop}
                  />
                </div>
              )}

              {/* Departure Board Tab Content */}
              {!leftPanelCollapsed && planningTab === 'departure-board' && (
                <DepartureBoardPanel
                  selectedPort={selectedDeparturePort}
                  onPortSelect={setSelectedDeparturePort}
                  onPlanRoute={handleDepartureBoardPlanRoute}
                  onRouteHover={setHoveredRouteId}
                />
              )}
            </div>
          </div>

          {/* Mobile My Trips Panel (Layer 2) - Full Screen on Mobile */}
          <div
            className={`absolute top-0 left-0 bottom-0 z-20 transition-all duration-300 w-full lg:hidden ${activeTab === 'trips' ? 'block' : 'hidden'} overflow-y-auto border border-gray-200`}
            style={{backgroundColor: '#F4F5F7'}}
          >
            <ItineraryPanel
              isOpen={true}
              onClose={() => {}}
              isEmbedded={true}
            />
          </div>

          {/* Desktop Draggable My Trips Panel (Layer 3) - Only on Desktop */}
          <DraggablePanel
            initialPosition={{ x: Math.max(windowWidth - 450, 24), y: 80 }}
            storageKey="my-trips-panel"
            isVisible={windowWidth >= 1024}
            className="w-96 max-w-[90vw]"
          >
            <ItineraryPanel
              isOpen={true}
              onClose={() => {}}
              isEmbedded={true}
            />
          </DraggablePanel>
        </div>
      </main>
    </>
  );
};
